<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { computed, PropType } from 'vue';
import dialog_bgImg from '@/assets/dialog_bg.png';
import dialog_bgImgDark from '@/assets/dialog_bg_dark.png';
import AppButton from '@/core/components/common/app-button.vue';

const emit = defineEmits(['save', 'cancel']);
const isVisible = defineModel<boolean>('isVisible');

const isDark = useDark();

const props = defineProps({
  icon: {
    type: String,
    default: 'hugeicons:pencil-edit-02',
  },
  title: {
    type: String,
  },
  contentTitle: {
    type: String,
  },
  contentSubtitle: {
    type: String,
  },
  contentSubtitleParams: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
  width: {
    type: [Number, String],
    default: 600,
  },
  height: {
    type: [Number, String],
    default: null,
  },
  maxHeight: {
    type: [Number, String],
    default: null,
  },
  saveLabel: {
    type: String,
  },
  useHeaderImage: {
    type: Boolean,
    default: true,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  useScroll: {
    type: Boolean,
    default: false,
  },
  hideActionButtonsIcon: {
    type: Boolean,
    default: false,
  },
});

const onSave = () => emit('save');

const headerStyle = computed(() => {
  return props.useHeaderImage
    ? {
        backgroundImage: `url(${isDark.value ? dialog_bgImgDark : dialog_bgImg})`,
      }
    : {};
});

const dialogStyle = computed(() => {
  const style: Record<string, string> = {};

  style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;

  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height;
    style.maxHeight = style.height;
  }

  if (props.maxHeight) {
    style.maxHeight = typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight;
  }

  return style;
});

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};
</script>
<template>
  <Dialog
    modal
    :visible="isVisible"
    :closable="false"
    :style="dialogStyle"
    :ptOptions="{ mergeSections: true, mergeProps: true }"
    :pt="{
      headeractions: {
        class: ['app-dialog-close-btn-parent'],
      },
    }"
  >
    <template #container>
      <div class="flex flex-col w-full pb-2 bg-white dark:bg-surface-900 rounded-lg" :class="{ 'h-full': props.height || props.maxHeight }">
        <!------------
          -- Header -- 
          ------------>
        <div class="flex !w-full justify-between pr-3">
          <div
            class="flex w-[216px] bg-cover bg-no-repeat"
            :style="headerStyle"
            :class="props.useHeaderImage ? 'h-[190px]' : 'h-[105px]'"
          >
            <div class="p-[2px]" :class="{ 'ml-[21px] mt-[20px]': !!props.title, 'ml-[26px] mt-[25px]': !props.title }">
              <slot name="icon">
                <Icon
                  :icon="props.icon"
                  class="w-10 h-10 rounded-lg border p-[6px] dark:text-white"
                  v-if="props.useHeaderImage"
                />
              </slot>
            </div>
          </div>
          <div class="w-full flex grow -ml-[90px] mt-[32px]" v-if="props.title">
            <span class="text-gray-900 ml-4 text-lg dark:text-gray-100 font-semibold">{{ $t(props.title) }}</span>
          </div>
          <div class="flex mt-[20px]">
            <app-button severity="regular" :outlined="false" icon="hugeicons:cancel-01" @click="onCancel" class="mr-1" />
          </div>
        </div>

        <!-------------
          -- Content --
          ------------->
        <div
          class="w-full dark:text-gray-100 pr-6 -mt-[80px] text-lg pl-6"
          :class="{ '!pr-3': useScroll, '-mt-[101px]': !title?.length, 'flex flex-col flex-1 min-h-0': props.height || props.maxHeight }"
        >
          <!-----------
            -- Title --
            ----------->
          <div class="text-lg font-semibold" v-if="props.contentTitle">
            {{ $t(props.contentTitle) }}
          </div>

          <!---------------
            -- Substitle --
            --------------->
          <div class="mt-2 text-sm font-regular text-gray-600 dark:text-slate-400" v-if="props.contentSubtitle">
            {{ $t(props.contentSubtitle, contentSubtitleParams) }}
          </div>

          <div :class="{ 'flex-1 overflow-y-auto min-h-0': props.height || props.maxHeight }">
            <slot> </slot>
          </div>

          <!--------------------
            -- Action Buttons --
            -------------------->
          <div class="flex justify-between gap-x-3 mt-5" :class="{ 'mb-3': !props.hideActions }">
            <slot name="actions">
              <div class="flex gap-2 w-full">
                <div class="flex-1">
                  <app-button
                    :icon="props.hideActionButtonsIcon ? '' : 'hugeicons:eraser-01'"
                    label="core.cancel2"
                    outlined
                    cssClass="w-full"
                    :severity="'regular'"
                    @click="isVisible = false"
                    v-if="!props.hideActions"
                    :reverse-icon-direction="!props.hideActionButtonsIcon"
                  />
                </div>
                <div class="flex-1">
                  <app-button
                    v-if="!props.hideActions"
                    :icon="props.hideActionButtonsIcon ? '' : 'hugeicons:floppy-disk'"
                    :outlined="false"
                    severity="primary"
                    cssClass="w-full"
                    :label="props.saveLabel || 'core.save'"
                    is-primary
                    @click="onSave"
                  />
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
